import { type ReactNode } from 'react';

interface IngredientAnchorWrapperProps {
  onClick: Props['onClick'];
  isRecipes: Props['isRecipesPage'];
  ingredient: {
    slug: string;
    id: number;
  };
  children: ReactNode;
}

const IngredientAnchorWrapper = (props: IngredientAnchorWrapperProps) => {
  const { onClick, isRecipes, ingredient, children } = props;
  const { id: ingredientId, slug: ingredientSlug } = ingredient;
  const testid = `ingredient-${ingredientId}`;
  function selectIngredient() {
    if (onClick === undefined) {
      return;
    }
    onClick(ingredientId);
  }

  const href = `/products/${ingredientSlug}/`;

  if (isRecipes === true) {
    return (
      <a
        data-testid={testid}
        href={href}
        className="itemContainer hr-pointer anchor_black"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </a>
    );
  }

  return (
    <div
      data-testid={testid}
      className={`itemContainer ${onClick ? 'hr-pointer' : ''}`}
      onClick={selectIngredient}
    >
      {children}
    </div>
  );
};

interface Props {
  onClick?: (ingredientId: number) => void;
  getPointsDiff?: (ingredientId: number) => void | number;
  openDrawer?: (ingredientId: number) => void;
  isRecipesPage?: boolean;
  ingredients: Array<{
    slug: string;
    id: number;
    name: string;
    brand_name: string;
    amount: number;
    packaged_image_url: string | null;
  }>;
  swappableIngredients?: Record<number, unknown[]>;
}

function ingredientPointsDiff(
  getPointsDiff: Props['getPointsDiff'],
  ingredientId: number
): number | null {
  if (getPointsDiff === undefined) {
    return null;
  }

  const result = getPointsDiff(ingredientId);
  if (result === undefined) {
    return null;
  }

  return result;
}

interface EditButonProps {
  swappableIngredients: Props['swappableIngredients'];
  openDrawer: Props['openDrawer'];
  ingredientId: number;
}

function renderEditButton(props: EditButonProps): ReactNode | null {
  const { swappableIngredients, openDrawer, ingredientId } = props;
  if (swappableIngredients === undefined || openDrawer === undefined) {
    return null;
  }

  const swappable = swappableIngredients[ingredientId];
  if (swappable === undefined || swappable.length === 0) {
    return null;
  }

  return (
    <div
      className="swap-item hr-pointer"
      onClick={() => openDrawer(ingredientId)}
    >
      <span
        data-testid={`edit-pen-${ingredientId}`}
        className="edit-pen"
      ></span>
    </div>
  );
}

export function Ingredients(props: Props) {
  const {
    ingredients,
    swappableIngredients,
    openDrawer,
    onClick,
    getPointsDiff,
    isRecipesPage,
  } = props;

  return (
    <div data-testid="ingredients-list" className="ingredients">
      <div className="hr-mb-8 hr-paragraph-bold ingredients-header-title">
        Ingredients
      </div>
      <div className="ingredients-list">
        {ingredients.map((ingredient, i) => {
          const {
            id: ingredientId,
            name: ingredientName,
            packaged_image_url,
            brand_name,
            amount,
          } = ingredient;

          const diffPoints = ingredientPointsDiff(getPointsDiff, ingredientId);
          const testid = `ingredient-${ingredientId}`;
          return (
            <IngredientAnchorWrapper
              onClick={onClick}
              isRecipes={isRecipesPage}
              ingredient={ingredient}
              key={`${ingredientName}-${i}`}
            >
              {packaged_image_url !== null && (
                <img
                  data-testid={`${testid}-image`}
                  data-id={ingredientId}
                  className="thumbnailImg"
                  src={packaged_image_url}
                  alt="food-img"
                />
              )}
              <div className="itemInfoContainer">
                <div
                  data-testid={`${testid}-brand-name`}
                  className="brandName hr-subtitle-sm"
                >
                  {brand_name}
                </div>
                <div
                  data-testid={`${testid}-name`}
                  data-id={ingredientId}
                  className="ingredient-name hr-subtitle-md-bold"
                >
                  {ingredientName}
                </div>
              </div>
              {amount > 1 && !diffPoints && !swappableIngredients ? (
                <div className="item-qty">
                  <span className="mul-icon"></span>
                  <span data-testid={`${testid}-amount`}>{` ${amount}`}</span>
                </div>
              ) : null}
              {renderEditButton({
                ingredientId,
                openDrawer,
                swappableIngredients,
              })}
              {
                // Only on customization:
                typeof diffPoints === 'number' ? (
                  <div
                    data-testid={`${testid}-points-diff`}
                    className="swap-item"
                  >
                    {(diffPoints >= 0 ? '+' : '') + diffPoints}&nbsp;
                    <span className="points-icon"></span>
                  </div>
                ) : null
              }
            </IngredientAnchorWrapper>
          );
        })}
      </div>
    </div>
  );
}
